import * as DG from "ui/dialogs"
import {
  ComponentPropsWithoutRef,
  useRef,
  useState,
  MouseEvent,
  useEffect,
} from "react"

import { css, cx } from "@emotion/css"

import Button from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { ReactComponent as ClockIcon } from "assets/icon-clock.svg"
import { HourInterval } from "models/packages"
import { HourRangePicker } from "components/HourPicker/HourRangePicker"
import Popover from "@material-ui/core/Popover"
import { hourLabelFromTime } from "."
import { uniqueId } from "lodash/fp"

type Props = {
  className?: string
  label?: string
  hasStartAdornment?: boolean
  asTextButton?: boolean
  clearLabel?: string
  submitLabel?: string
  onRangeChange: (hourInterval: HourInterval) => void
  onRangeClear?: () => void
  pickerSize?: "small" | "default"
} & Omit<ComponentPropsWithoutRef<typeof HourRangePicker>, "onChange" | "size">

export function HourRangeButtonTrigger(props: Props) {
  const {
    className,
    label = "",
    hasStartAdornment,
    asTextButton,
    clearLabel = "Clear",
    submitLabel = "Submit",
    start = "",
    end = "",
    disabled,
    pickerSize,
    onRangeChange,
    onRangeClear,
    ...rangePickerProps
  } = props

  const { current: popoverId } = useRef(uniqueId("hourRangePopoverPicker-"))
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const [hourRange, setHourRange] = useState<HourInterval>({ start, end })

  useEffect(() => {
    setHourRange({ start, end })
  }, [end, start])

  const handleHourRangeChange = ({
    name,
    value,
  }: {
    name: string
    value?: string
  }) => {
    setHourRange((prev) => ({ ...prev, [name]: value }))
  }

  const handleClear = () => {
    handleClose()
    setHourRange({})
    onRangeChange({})
    onRangeClear?.()
  }

  const handleSubmit = () => {
    handleClose()
    onRangeChange(hourRange)
  }

  const displayValue =
    start && end
      ? `${hourLabelFromTime(start)} - ${hourLabelFromTime(end)}`
      : label

  const popoverOpen = Boolean(anchorEl)
  const hourRangePopoverId = popoverOpen ? popoverId : undefined

  return (
    <>
      <button
        onClick={handleOpen}
        disabled={disabled}
        className={cx(
          styles.standardButton,
          asTextButton && styles.textButton,
          anchorEl &&
            css`
              color: var(--primary-main, #00b1a4);
              .icon {
                color: var(--primary-main, #00b1a4);
              }
            `,
          className,
        )}
      >
        {hasStartAdornment && (
          <span className="icon icon-start">
            <ClockIcon className={styles.icon} />
          </span>
        )}
        <span>{displayValue}</span>
      </button>
      <Popover
        id={hourRangePopoverId}
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: -8,
          horizontal: "left",
        }}
      >
        <DG.DialogContent paddingTop className={styles.popoverContent}>
          <span className="BodyBody-1BlackBold">{label}</span>
          <HourRangePicker
            size={pickerSize}
            start={hourRange.start ?? ""}
            end={hourRange.end ?? ""}
            disabled={disabled}
            onChange={handleHourRangeChange}
            {...rangePickerProps}
          />
        </DG.DialogContent>
        <DG.DialogFooter>
          <DG.DialogActions>
            <ButtonGroup>
              <Button variant="outlined" color="primary" onClick={handleClear}>
                {clearLabel}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={hourRange.start === "" || hourRange.end === ""}
              >
                {submitLabel}
              </Button>
            </ButtonGroup>
          </DG.DialogActions>
        </DG.DialogFooter>
      </Popover>
    </>
  )
}

const styles = {
  standardButton: css`
    position: relative;
    display: inline-flex;
    box-sizing: border-box;
    font-family: inherit;
    white-space: nowrap;
    vertical-align: middle;
    cursor: pointer;
    user-select: none;
    appearance: none;
    text-decoration: none;
    outline: none;
    text-align: center;
    align-items: center;

    background: transparent;
    border-radius: 4px;

    font-size: var(--font-size, 1.125rem);
    font-weight: var(--font-weight, normal);
    padding: 4px;

    border-color: transparent;
    border-width: 1px;
    border-style: solid;

    transition:
      background-color,
      border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

    &:enabled:hover {
      border-color: var(--primary-main, #00b1a4);
      .icon {
        color: var(--black, #000);
      }
    }
    &:focus {
      border-color: var(--primary-main, #00b1a4);
    }

    .icon {
      color: var(--dark-grey, #808080);
    }

    &:disabled .icon {
      opacity: 0.6;
    }

    .icon-start {
      margin-inline-start: -4px;
      margin-inline-end: 4px;
    }
  `,
  textButton: css`
    font-size: 0.8125rem;
    font-weight: 400;
    color: var(--black);

    &:enabled:hover {
      border-color: transparent;
      .icon {
        color: var(--black, #000);
      }
    }
    &:focus {
      border-color: transparent;
      color: var(--primary-main, #00b1a4);
      .icon {
        color: var(--primary-main, #00b1a4);
      }
    }
  `,
  icon: css`
    min-width: var(--spacing-xl);
    height: var(--spacing-xl);
    padding-top: 2px;
  `,
  popoverContent: css`
    display: flex;
    align-items: center;
    gap: var(--spacing-xxl-2);
  `,
}
